import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, Clock, CheckCircle, Trophy, Star, UserPlus, UserMinus, ChevronRight, Radio, Zap, ArrowLeft, RefreshCw } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { getTeamFlag } from '../utils/teamFlags';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  live: { label: 'LIVE', color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/20', dot: 'bg-red-500 animate-pulse' },
  starting_soon: { label: 'SOON', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/15', dot: 'bg-amber-500' },
  today: { label: 'TODAY', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/15', dot: 'bg-sky-500' },
  upcoming: { label: 'UPCOMING', color: 'text-emerald-400', bg: 'bg-emerald-500/8 border-emerald-500/15', dot: 'bg-emerald-500' },
  finished: { label: 'FT', color: 'text-white/50', bg: 'bg-white/[0.03] border-white/[0.06]', dot: 'bg-white/30' },
  unknown: { label: '', color: 'text-white/40', bg: 'bg-white/[0.02] border-white/[0.05]', dot: 'bg-white/20' },
};

export default function MatchFeed() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchFeed = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/match-feed`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      setData(json);
    } catch { /* silent */ }
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // Auto-refresh every 60s if there are live matches
  useEffect(() => {
    if (!data?.counts?.live) return;
    const interval = setInterval(() => fetchFeed(), 60000);
    return () => clearInterval(interval);
  }, [data?.counts?.live, fetchFeed]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const sections = [
    { key: 'live_now', title: 'Live Now', icon: Radio, iconColor: 'text-red-400', matches: data?.live_now || [], emptyMsg: 'No live matches right now' },
    { key: 'starting_soon', title: 'Starting Soon', icon: Clock, iconColor: 'text-amber-400', matches: data?.starting_soon || [], emptyMsg: null },
    { key: 'today', title: "Today's Matches", icon: Zap, iconColor: 'text-sky-400', matches: data?.today || [], emptyMsg: null },
    { key: 'recently_finished', title: 'Recent Results', icon: Trophy, iconColor: 'text-white/50', matches: data?.recently_finished || [], emptyMsg: null },
  ];

  const hasAny = sections.some(s => s.matches.length > 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="match-feed-page">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-b from-[#111] to-[#0a0a0a] border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="feed-back-btn">
                <ArrowLeft className="w-4 h-4 text-white/60" />
              </Link>
              <Activity className="w-5 h-5 text-emerald-500" />
              <h1 className="text-white font-bold text-lg">Match Feed</h1>
            </div>
            <button
              onClick={() => fetchFeed(true)}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full text-white/50 text-xs font-medium hover:text-white hover:bg-white/[0.08] transition-colors"
              data-testid="feed-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Live count highlight */}
          {(data?.counts?.live || 0) > 0 && (
            <div className="mt-3 flex items-center gap-2" data-testid="live-count-badge">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-400 text-xs font-bold">{data.counts.live} match{data.counts.live !== 1 ? 'es' : ''} live now</span>
            </div>
          )}

          {/* Summary */}
          <div className="flex items-center gap-3 mt-3 text-white/30 text-[11px]">
            <span>{data?.total || 0} matches</span>
            {data?.followed_teams?.length > 0 && (
              <>
                <span>&middot;</span>
                <span className="text-emerald-400/60">Following: {data.followed_teams.join(', ')}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Feed sections */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
        {!hasAny && (
          <div className="text-center py-16" data-testid="feed-empty">
            <Activity className="w-12 h-12 text-white/10 mx-auto mb-3" />
            <p className="text-white/50 text-sm">No matches available right now</p>
            <p className="text-white/30 text-xs mt-1">Check back later or browse the calendar</p>
            <Link to="/calendar" className="inline-block mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors">
              View Calendar
            </Link>
          </div>
        )}

        {sections.map(section => {
          if (section.matches.length === 0 && !section.emptyMsg) return null;
          const Icon = section.icon;
          return (
            <div key={section.key} data-testid={`feed-section-${section.key}`}>
              {/* Section header */}
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`w-4 h-4 ${section.iconColor}`} />
                <h2 className="text-white font-bold text-sm uppercase tracking-wider">{section.title}</h2>
                <span className="text-white/20 text-xs ml-1">{section.matches.length}</span>
              </div>

              {section.matches.length === 0 && section.emptyMsg ? (
                <div className="py-6 text-center rounded-xl bg-white/[0.02] border border-white/5">
                  <p className="text-white/30 text-sm">{section.emptyMsg}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {section.matches.map((m, i) => (
                    <MatchCard key={m.id || `${m.home}-${m.away}-${i}`} match={m} navigate={navigate} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MatchCard({ match, navigate }) {
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowed, setIsFollowed] = useState(match.is_followed);
  const homeFlag = getTeamFlag(match.home);
  const awayFlag = getTeamFlag(match.away);
  const cfg = STATUS_CONFIG[match.match_status] || STATUS_CONFIG.unknown;
  const hasScore = match.score && match.score.home !== undefined && match.score.home !== null;

  const toggleFollow = async (e, teamName) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) return;
    setFollowLoading(true);
    try {
      const endpoint = isFollowed ? 'unfollow-team' : 'follow-team';
      await fetch(`${API}/api/user/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ team_name: teamName }),
      });
      setIsFollowed(!isFollowed);
    } catch { /* silent */ }
    finally { setFollowLoading(false); }
  };

  return (
    <div
      className={`p-3 sm:p-4 rounded-xl border transition-all cursor-pointer group hover:bg-white/[0.03] ${cfg.bg}`}
      onClick={() => { if (match.id) navigate(`/matchday/${match.id}`); }}
      data-testid={`feed-match-${match.id || 'ext'}`}
    >
      <div className="flex items-start gap-3">
        {/* Status indicator */}
        <div className="flex flex-col items-center gap-1 pt-1 shrink-0 w-10">
          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
          <span className={`text-[9px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
        </div>

        {/* Match content */}
        <div className="flex-1 min-w-0">
          {/* Teams + score row */}
          <div className="space-y-1.5">
            {/* Home */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {homeFlag ? (
                  <span className="text-base shrink-0">{homeFlag}</span>
                ) : (
                  <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[7px] font-bold text-white/40 shrink-0">
                    {match.home.slice(0, 3).toUpperCase()}
                  </span>
                )}
                <span className={`font-semibold text-sm truncate ${match.match_status === 'live' ? 'text-white' : 'text-white/80'}`}>
                  {match.home}
                </span>
                {match.is_followed && (
                  <Star className="w-3 h-3 text-amber-400 shrink-0" />
                )}
              </div>
              {hasScore && (
                <span className={`text-lg font-black tabular-nums shrink-0 ${match.match_status === 'live' ? 'text-white' : 'text-white/60'}`}>
                  {match.score.home}
                </span>
              )}
            </div>
            {/* Away */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {awayFlag ? (
                  <span className="text-base shrink-0">{awayFlag}</span>
                ) : (
                  <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[7px] font-bold text-white/40 shrink-0">
                    {match.away.slice(0, 3).toUpperCase()}
                  </span>
                )}
                <span className={`font-semibold text-sm truncate ${match.match_status === 'live' ? 'text-white' : 'text-white/80'}`}>
                  {match.away}
                </span>
              </div>
              {hasScore && (
                <span className={`text-lg font-black tabular-nums shrink-0 ${match.match_status === 'live' ? 'text-white' : 'text-white/60'}`}>
                  {match.score.away}
                </span>
              )}
            </div>
          </div>

          {/* Key events (goals, red cards) — only for real data */}
          {match.events && match.events.length > 0 && match.data_source !== 'simulated' && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`feed-events-${match.id}`}>
              {match.events.map((evt, i) => (
                <span
                  key={i}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                    evt.type === 'goal'
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-red-500/15 text-red-400'
                  }`}
                >
                  {evt.minute}' {evt.type === 'goal' ? 'Goal' : 'Red Card'}
                </span>
              ))}
            </div>
          )}

          {/* Simulated label */}
          {match.data_source === 'simulated' && match.match_status !== 'upcoming' && match.match_status !== 'unknown' && (
            <span className="inline-block mt-1.5 px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/15 rounded text-[8px] text-amber-400/70 font-medium" data-testid={`feed-simulated-${match.id}`}>
              Sample Data
            </span>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {match.competition && (
              <span className="text-emerald-400/60 text-[10px] font-medium">{match.competition}</span>
            )}
            {match.time && (
              <span className="text-white/25 text-[10px]">
                <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                {formatTime(match.time)}
              </span>
            )}
            {match.venue && (
              <span className="text-white/20 text-[10px] truncate max-w-[150px]">{match.venue}</span>
            )}
            {match.has_real_data && (
              <span className="flex items-center gap-0.5 text-emerald-400/50 text-[9px]" data-testid={`feed-verified-${match.id}`}>
                <CheckCircle className="w-2.5 h-2.5" /> Verified
              </span>
            )}
          </div>
        </div>

        {/* Follow button */}
        {localStorage.getItem('token') && (
          <button
            onClick={(e) => toggleFollow(e, match.home)}
            disabled={followLoading}
            className={`shrink-0 p-1.5 rounded-lg transition-all ${
              isFollowed
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-white/[0.04] text-white/20 hover:text-white/50 hover:bg-white/[0.08]'
            }`}
            data-testid={`feed-follow-${match.id || 'ext'}`}
          >
            {isFollowed ? <UserMinus className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
          </button>
        )}

        {/* Arrow */}
        {match.id && (
          <ChevronRight className="w-4 h-4 text-white/10 group-hover:text-white/30 transition-colors shrink-0 mt-2" />
        )}
      </div>
    </div>
  );
}


function formatTime(timeStr) {
  if (!timeStr) return '';
  try {
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setUTCHours(parseInt(h), parseInt(m));
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return timeStr;
  }
}
